import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui'
import { ChannelPairingCard } from '@/features/onboarding/components/ChannelPairingCard'
import { useChannelPairing } from '@/features/onboarding/useChannelPairing'

import { channelsQueryKey } from '../queries'

export interface ChannelReconnectPanelProps {
  readonly tenantId: string
  readonly channelId: string
  readonly onClose: () => void
}

/**
 * Painel de reconexao do canal: reusa integralmente a experiencia de QR do onboarding
 * (`ChannelPairingCard` — QR em memoria, contagem regressiva, expiracao e "gerar novo codigo"),
 * emoldurada pelas mesmas instrucoes curtas ("abrir WhatsApp -> Aparelhos conectados -> apontar a
 * camera"). O QR nunca e logado nem persistido (docs/06 s4, CLAUDE.md): so vive no cache do
 * TanStack Query, dentro do `ChannelPairingCard`.
 *
 * `useChannelPairing` aqui compartilha a MESMA entrada de cache que o card (mesma queryKey), entao
 * nao dispara um segundo request — serve apenas para, quando o pareamento conclui (`Connected`),
 * invalidar a lista de canais para o status "Conectado" aparecer sem o usuario recarregar.
 */
export function ChannelReconnectPanel({
  tenantId,
  channelId,
  onClose,
}: ChannelReconnectPanelProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const pairingQuery = useChannelPairing(tenantId, channelId)
  const connected = pairingQuery.data?.status === 'Connected'

  useEffect(() => {
    if (connected) {
      void queryClient.invalidateQueries({ queryKey: channelsQueryKey(tenantId) })
    }
  }, [connected, queryClient, tenantId])

  return (
    <div className="flex flex-col gap-5 rounded-lg border border-border-default bg-background-subtle p-5 sm:flex-row sm:gap-8">
      <div className="flex flex-1 flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-semibold text-foreground-primary">
            {t('channels.reconnect.title')}
          </h4>
          <p className="text-sm leading-relaxed text-foreground-secondary">
            {t('channels.reconnect.description')}
          </p>
        </div>
        <ol className="flex list-decimal flex-col gap-2 pl-4 text-sm leading-relaxed text-foreground-secondary marker:text-foreground-muted">
          <li>{t('onboarding.connect.instructions1')}</li>
          <li>{t('onboarding.connect.instructions2')}</li>
          <li>{t('onboarding.connect.instructions3')}</li>
        </ol>
        <Button variant="ghost" size="sm" className="self-start" onClick={onClose}>
          {t('channels.card.close')}
        </Button>
      </div>

      <div className="flex items-start justify-center sm:justify-end">
        <ChannelPairingCard tenantId={tenantId} channelId={channelId} />
      </div>
    </div>
  )
}
